import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, API, getToken } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { fmtDuration, fmtDateTime } from "../lib/format";
import { FileSpreadsheet, FileDown } from "lucide-react";

const now = new Date();
const dateValue = (date) => date.toISOString().slice(0, 10);

function SummaryCell({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">{label}</div>
      <div className="font-heading font-black tracking-tighter text-2xl">{value}</div>
    </div>
  );
}

export default function AdminReports() {
  const [from, setFrom] = useState(dateValue(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [to, setTo] = useState(dateValue(now));
  const [customer, setCustomer] = useState("");
  const [statuses, setStatuses] = useState([]);

  const statusesQ = useQuery({ queryKey: ["ticket-statuses"], queryFn: () => api.get("/ticket-statuses").then((r) => r.data) });

  const { data, isLoading } = useQuery({
    queryKey: ["report", from, to, customer, statuses],
    queryFn: () => api.get("/admin/reports/monthly", { params: { from, to, customer: customer || undefined, statuses } }).then((r) => r.data),
  });

  const s = data?.summary;
  const rows = data?.rows || [];

  const exportUrl = (format) => {
    const params = new URLSearchParams({ format, from, to, token: getToken() });
    if (customer) params.set("customer", customer);
    statuses.forEach((status) => params.append("statuses[]", status));
    return `${API}/admin/reports/monthly/export?${params.toString()}`;
  };

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-black tracking-tighter text-4xl">Ticket Report</h1>
          <p className="text-muted-foreground mt-1">Detail SLA berdasarkan periode, status, dan customer</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" data-testid="export-csv" asChild>
            <a href={exportUrl("csv")}><FileSpreadsheet className="h-4 w-4" /> CSV</a>
          </Button>
          <Button variant="outline" className="gap-2" data-testid="export-pdf" asChild>
            <a href={exportUrl("pdf")}><FileDown className="h-4 w-4" /> PDF</a>
          </Button>
        </div>
      </div>

      <div className="mb-6 grid gap-4 rounded-lg border border-border bg-card p-4 md:grid-cols-4">
        <label className="space-y-2 text-sm font-medium">Tanggal mulai<Input type="date" value={from} max={to} onChange={(event) => setFrom(event.target.value)} /></label>
        <label className="space-y-2 text-sm font-medium">Tanggal selesai<Input type="date" value={to} min={from} onChange={(event) => setTo(event.target.value)} /></label>
        <label className="space-y-2 text-sm font-medium">Customer<Input value={customer} onChange={(event) => setCustomer(event.target.value)} placeholder="Cari nama customer" /></label>
        <div className="space-y-2 text-sm font-medium"><span>Status</span><div className="flex max-h-24 flex-wrap gap-x-3 gap-y-2 overflow-y-auto rounded-md border border-input p-2">{(statusesQ.data || []).map((status) => <label key={status.code} className="flex items-center gap-2 text-xs font-normal"><input type="checkbox" checked={statuses.includes(status.code)} onChange={(event) => setStatuses(event.target.checked ? [...statuses, status.code] : statuses.filter((code) => code !== status.code))} />{status.label}</label>)}</div></div>
      </div>

      {isLoading || !s ? (
        <div className="p-10 text-center text-muted-foreground">Loading report…</div>
      ) : (
        <>
          <div className="mb-2 text-sm font-medium text-muted-foreground">Periode {s.from} sampai {s.to}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6" data-testid="report-summary">
            <SummaryCell label="Total" value={s.total} />
            <SummaryCell label="Completed" value={s.completed} />
            <SummaryCell label="Open" value={s.open} />
            <SummaryCell label="In Progress" value={s.in_progress} />
            <SummaryCell label="SLA Met" value={s.sla_met} />
            <SummaryCell label="SLA Breached" value={s.sla_breached} />
            <SummaryCell label="SLA %" value={`${s.sla_pct}%`} />
            <SummaryCell label="Avg Resolution" value={fmtDuration(s.avg_resolution_seconds)} />
            <SummaryCell label="Fastest" value={fmtDuration(s.fastest_seconds)} />
            <SummaryCell label="Longest" value={fmtDuration(s.longest_seconds)} />
            <SummaryCell label="Avg High" value={fmtDuration(s.avg_high_seconds)} />
            <SummaryCell label="Avg Medium" value={fmtDuration(s.avg_medium_seconds)} />
          </div>

          <div className="rounded-lg border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-muted-foreground">
                <tr className="text-left">
                  <th className="px-4 py-3 font-medium">Ticket</th>
                  <th className="px-4 py-3 font-medium">Subject</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Assignee</th>
                  <th className="px-4 py-3 font-medium">Priority</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                  <th className="px-4 py-3 font-medium">Started</th>
                  <th className="px-4 py-3 font-medium">Done</th>
                  <th className="px-4 py-3 font-medium">Resolution</th>
                  <th className="px-4 py-3 font-medium">SLA Target</th>
                  <th className="px-4 py-3 font-medium">Deadline</th>
                  <th className="px-4 py-3 font-medium">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.length === 0 ? (
                  <tr><td colSpan={13} className="px-4 py-10 text-center text-muted-foreground">No tickets in this period.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.ticket_number} className="hover:bg-secondary/40">
                    <td className="px-4 py-3 font-mono-plex text-xs">{r.ticket_number}</td>
                    <td className="max-w-[220px] truncate px-4 py-3">{r.subject || "-"}</td>
                    <td className="px-4 py-3 max-w-[150px] truncate">{r.customer}</td>
                    <td className="px-4 py-3">{r.assignee || "-"}</td>
                    <td className="px-4 py-3">{r.priority}</td>
                    <td className="px-4 py-3">{r.status}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDateTime(r.started_at)}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDateTime(r.done_at)}</td>
                    <td className="px-4 py-3 font-mono-plex text-xs">{fmtDuration(r.resolution_seconds)}</td>
                    <td className="px-4 py-3 font-mono-plex text-xs">{fmtDuration(r.sla_target_seconds)}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{r.sla_deadline ? fmtDateTime(r.sla_deadline) : "-"}</td>
                    <td className={`px-4 py-3 font-semibold text-xs ${r.sla_result === "MET" ? "text-emerald-600" : r.sla_result === "BREACHED" ? "text-red-600" : "text-muted-foreground"}`}>{r.sla_result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Layout>
  );
}
